/**
 * Declarative route configuration that maps paths to:
 * - page titles (for document.title)
 * - required permissions (for access control)
 * - sidebar section/group membership (for open state)
 *
 * This replaces the large switch/case and if/else chains in AuthenticatedLayout.
 */

export const ROUTE_CONFIG = [
    { path: '/contacts', title: 'Contact Inventory', permission: 'contacts', group: 'contacts' },
    { path: '/custom-fields', title: 'Custom Fields', permission: 'contacts', group: 'contacts' },
    { path: '/contact-import', title: 'Contact Import', permission: 'inventoryManagement', group: 'management' },
    { path: '/bulk-edit', title: 'Bulk Management', permission: 'inventoryManagement', group: 'management' },
    { path: '/segments', title: 'Contact Segmentation', permission: 'inventoryManagement', group: 'management' },
    { path: '/field-mappings', title: 'Field Mappings', permission: 'inventoryManagement', group: 'management' },
    { path: '/caller-id/numbers', title: 'Number Management', permission: 'callerId', group: 'callerId' },
    { path: '/caller-id/configurations', title: 'Configurations', permission: 'callerId', group: 'callerId' },
    { path: '/campaigns', title: 'Campaigns', permission: 'campaigns', group: null },
    { path: '/callback-configurations', title: 'Callback Configurations', permission: 'campaigns', group: null },
    { path: '/profiles', title: 'Communication Profiles', permission: 'communicationProfiles', group: null },
    { path: '/message-templates', title: 'Message Templates', permission: 'digitalChannels', group: 'omni' },
    { path: '/digital-profiles', title: 'Digital Profiles', permission: 'digitalChannels', group: 'omni' },
    { path: '/suppression', title: 'Suppression Profiles', permission: 'suppression', group: 'suppression' },
    { path: '/dnc-lists', title: 'DNC', permission: 'suppression', group: 'suppression' },
    { path: '/contact-field-suppression', title: 'Contact Field Suppression', permission: 'suppression', group: 'suppression' },
    { path: '/approver', title: 'Live Agent Select', permission: 'liveAgentSelect', group: null },
    { path: '/connection-details', title: 'Connection Details', permission: 'connection', group: 'connection' },
    { path: '/disposition-mappings', title: 'Disposition Mappings', permission: 'connection', group: 'connection' },
    { path: '/users', title: 'Users', permission: 'userManagement', group: null },
    { path: '/notification-groups', title: 'Notification Groups', permission: 'userManagement', group: null },
    { path: '/audit-log', title: 'Audit Log', permission: 'auditLog', group: null },
];

/**
 * Find the matching route config for a given pathname.
 */
export function findRouteConfig(pathname) {
    return ROUTE_CONFIG.find((route) => pathname.includes(route.path));
}

/**
 * Check if the user has permission to access the given pathname.
 * Returns true if allowed, false if denied.
 */
export function hasPermission(pathname, securityOptions) {
    const route = findRouteConfig(pathname);
    if (!route) return false;
    return securityOptions[route.permission] >= 1;
}

/**
 * Ordered list of fallback pages for navigateToAllowedPage.
 */
const FALLBACK_ORDER = [
    { permission: 'contacts', path: '/contacts' },
    { permission: 'inventoryManagement', path: '/segments' },
    { permission: 'callerId', path: '/caller-id/numbers' },
    { permission: 'campaigns', path: '/campaigns' },
    { permission: 'communicationProfiles', path: '/profiles' },
    { permission: 'digitalChannels', path: '/message-templates' },
    { permission: 'suppression', path: '/suppression' },
    { permission: 'connection', path: '/connection-details' },
    { permission: 'userManagement', path: '/user-management' },
    { permission: 'auditLog', path: '/audit-log' },
];

/**
 * Returns the first allowed page path for the given security options.
 */
export function getFirstAllowedPage(securityOptions) {
    const entry = FALLBACK_ORDER.find((item) => securityOptions[item.permission] > 0);
    return entry ? entry.path : null;
}
